/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import { printOpdBill, printOpdInvoice, printOpdReceipt } from '../../opdBilling/print';
import BillQuickActionsMenu from '../../opdBilling/BillQuickActionsMenu';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EventNoteIcon from '@mui/icons-material/EventNote';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PhoneIcon from '@mui/icons-material/Phone';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import Swal from 'sweetalert2';
import MDButton from 'components/MDButton';
import { api } from '../../../api';
import {
  DROPDOWN_SX,
  GUEST_CHIP_COLOR,
  GUEST_CHIP_LABEL,
  STATUS_CHIP_COLOR,
  STATUS_HEX,
  STATUS_LABELS,
  TEAL,
  buildPrescriptionPrintUrl,
  formatAppointmentNo,
  formatDisplayDate,
  formatDisplayTime,
  formatFullDateTime,
  getCurrentUserId,
  resolveDoctorName,
  resolvePatientMobile,
  resolvePatientName,
} from '../constants';
import RescheduleDialog from './RescheduleDialog';
import CancelDialog from './CancelDialog';
import ReAppointmentDialog from './ReAppointmentDialog';
import CreateFollowupFromAppointmentDialog from './CreateFollowupFromAppointmentDialog';
import CreatePrescriptionFromAppointmentDialog from './CreatePrescriptionFromAppointmentDialog';

const DRAWER_WIDTH = 500;
// Payment section only makes sense once the patient has actually been seen
// through check-in — the OPD bill is auto-created at that point (see
// AppointmentService.checkIn()).
const BILLABLE_STATUSES = ['CHECKED_IN', 'ATTENDED', 'COMPLETED'];

const TIMELINE_META = {
  CREATED: { icon: AddCircleIcon, color: TEAL, label: 'Appointment Created' },
  STATUS_CHANGED: { icon: SwapHorizIcon, color: null, label: 'Status Changed' },
  DATE_CHANGED: { icon: EventNoteIcon, color: '#5c6bc0', label: 'Rescheduled' },
  SERIAL_CHANGED: { icon: SwapVertIcon, color: '#00897b', label: 'Serial Reordered' },
  NOTE_ADDED: { icon: StickyNote2Icon, color: '#455a64', label: 'Note Added' },
  PATIENT_MAPPED: { icon: LinkIcon, color: '#00897b', label: 'Linked to Patient' },
};

// A professional vertical timeline — colored icon-in-circle per event,
// connected by a line, with a title + detail line + timestamp footer.
function StatusTimeline({ logs }) {
  if (!logs || logs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="textSecondary">No history yet.</Typography>
      </Box>
    );
  }

  const entries = [...logs].reverse();

  return (
    <Box>
      {entries.map((log, index) => {
        const meta = TIMELINE_META[log.change_type] || TIMELINE_META.NOTE_ADDED;
        const color = meta.color || STATUS_HEX[log.new_status] || TEAL;
        const Icon = meta.icon;
        const isLast = index === entries.length - 1;

        return (
          <Box key={log.id} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: isLast ? 0 : 2.5 }}>
            {!isLast && (
              <Box sx={{ position: 'absolute', left: 15, top: 34, bottom: 0, width: 2, bgcolor: '#eceff1' }} />
            )}
            <Box
              sx={{
                width: 32,
                height: 32,
                minWidth: 32,
                borderRadius: '50%',
                bgcolor: `${color}18`,
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <Icon sx={{ color, fontSize: 16 }} />
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                bgcolor: '#fafbfc',
                border: '1px solid #eef0f2',
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#2d3748' }}>
                  {log.change_type === 'CREATED' && 'Appointment Created'}
                  {log.change_type === 'STATUS_CHANGED' && `Status changed to ${STATUS_LABELS[log.new_status] || log.new_status}`}
                  {log.change_type === 'DATE_CHANGED' && 'Rescheduled'}
                  {log.change_type === 'SERIAL_CHANGED' && 'Queue position updated'}
                  {log.change_type === 'NOTE_ADDED' && 'Note Added'}
                  {log.change_type === 'PATIENT_MAPPED' && 'Linked to Patient'}
                </Typography>
                {log.is_system_generated === 'Y' && (
                  <Chip label="system" size="small" sx={{ height: 16, fontSize: '0.55rem', flexShrink: 0 }} />
                )}
              </Box>

              {log.change_type === 'STATUS_CHANGED' && (
                <Typography sx={{ fontSize: '0.72rem', color: '#78909c', mt: 0.25 }}>
                  <s style={{ color: '#c5ccd1' }}>{STATUS_LABELS[log.old_status] || log.old_status}</s>
                  {' → '}
                  <b style={{ color }}>{STATUS_LABELS[log.new_status] || log.new_status}</b>
                </Typography>
              )}
              {log.change_type === 'DATE_CHANGED' && (
                <Typography sx={{ fontSize: '0.72rem', color: '#78909c', mt: 0.25 }}>
                  <s style={{ color: '#c5ccd1' }}>{formatDisplayDate(log.old_date)}</s> → <b style={{ color }}>{formatDisplayDate(log.new_date)}</b>
                </Typography>
              )}
              {log.change_type === 'SERIAL_CHANGED' && (
                <Typography sx={{ fontSize: '0.72rem', color: '#78909c', mt: 0.25 }}>
                  Serial #{log.old_serial_no} → <b style={{ color }}>#{log.new_serial_no}</b>
                </Typography>
              )}
              {(log.change_type === 'NOTE_ADDED' || log.change_type === 'PATIENT_MAPPED') && log.notes && (
                <Typography sx={{ fontSize: '0.72rem', color: '#78909c', mt: 0.25, fontStyle: 'italic' }}>
                  {log.notes}
                </Typography>
              )}
              {log.reason && (
                <Typography sx={{ fontSize: '0.7rem', color: '#9aa5b1', mt: 0.25 }}>
                  Reason: {log.reason}
                </Typography>
              )}

              <Typography sx={{ fontSize: '0.63rem', color: '#b0bac2', mt: 0.5 }}>
                {formatFullDateTime(log.created_at)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// A self-contained pill: icon + label side by side, on a solid colored
// background with forced-white text — avoids the earlier icon+caption
// layout where labels could wrap/overlap between adjacent buttons.
function ActionIcon({ title, icon, color, onClick }) {
  return (
    <Tooltip title={title} arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.85,
          borderRadius: 20,
          bgcolor: color,
          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
          cursor: 'pointer',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease',
          '&:hover': {
            filter: 'brightness(0.92)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.24)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Box sx={{ color: '#fff !important', display: 'flex', fontSize: 18 }}>{icon}</Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff !important', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function AppointmentDetailDrawer({ appointmentId, onClose, onChanged }) {
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null); // 'reschedule' | 'cancel' | 're-appointment' | 'followup' | 'prescription'
  const [openingPrescription, setOpeningPrescription] = useState(false);
  const [possibleMatches, setPossibleMatches] = useState(null); // null = not checked, [] = none found, [...] = candidates
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showMappingPanel, setShowMappingPanel] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [opdBill, setOpdBill] = useState(null);
  const [loadingBill, setLoadingBill] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payment_mode: 'CASH', amount: '', reference_no: '' });
  const [collectingPayment, setCollectingPayment] = useState(false);

  const load = useCallback(() => {
    if (!appointmentId) return;
    setLoading(true);
    api.appointment
      .getAppointmentById(appointmentId)
      .then(resp => setAppointment(resp?.data || null))
      .catch(() => setAppointment(null))
      .finally(() => setLoading(false));
  }, [appointmentId]);

  useEffect(() => {
    load();
    setShowMappingPanel(false);
    setPossibleMatches(null);
  }, [load]);

  // Silently check for independently-registered matches as soon as a guest
  // appointment's details are opened — the drawer surfaces the indicator on
  // its own, without the receptionist having to click "Mapping" first.
  useEffect(() => {
    if (!appointment || appointment.is_guest !== 'Y') return;
    setLoadingMatches(true);
    api.appointment
      .getPossibleMatches(appointment.id)
      .then(resp => setPossibleMatches(resp?.data || []))
      .catch(() => setPossibleMatches(null))
      .finally(() => setLoadingMatches(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment?.id, appointment?.is_guest]);

  const loadBill = useCallback(() => {
    if (!appointment || !BILLABLE_STATUSES.includes(appointment.status)) {
      setOpdBill(null);
      return;
    }
    setLoadingBill(true);
    api.opdBilling
      .getBillForAppointment(appointment.id)
      .then(resp => setOpdBill(resp?.data || null))
      .catch(() => setOpdBill(null))
      .finally(() => setLoadingBill(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment?.id, appointment?.status]);

  useEffect(() => {
    loadBill();
  }, [loadBill]);

  const refreshAndNotify = () => {
    load();
    onChanged?.();
  };

  const runAction = async (title, apiCall) => {
    const confirmResult = await Swal.fire({
      title,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Confirm',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;
    try {
      await apiCall();
      refreshAndNotify();
    } catch {
      // generic error toast already shown by ApiClient
    }
  };

  const handleCheckIn = () =>
    runAction('Check in this patient?', () =>
      api.appointment.checkIn(appointment.id, { updated_by: getCurrentUserId() }),
    );
  const handleAttend = () =>
    runAction('Mark as attended by the doctor?', () =>
      api.appointment.attend(appointment.id, { updated_by: getCurrentUserId() }),
    );
  const handleComplete = () =>
    runAction('Mark this appointment as completed?', () =>
      api.appointment.complete(appointment.id, { updated_by: getCurrentUserId() }),
    );

  const handleViewPrescription = async () => {
    if (!appointment?.irm_prescriptions_id) return;
    setOpeningPrescription(true);
    try {
      const resp = await api.opd.getPrescriptionById(appointment.irm_prescriptions_id);
      const code = resp?.data?.prescription_code;
      if (code) {
        window.open(buildPrescriptionPrintUrl(appointment, code), '_blank', 'noopener,noreferrer');
      }
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setOpeningPrescription(false);
    }
  };

  // Deep-links to the existing patient-registration screen, passing the
  // guest appointment id + captured name/mobile as GET query params. The
  // registration screen prefills from these and, on successful submit,
  // auto-tags the new contact back onto this appointment (mapPatient).
  const handleRegisterAsPatient = () => {
    if (!appointment) return;
    const params = new URLSearchParams({
      guestAppointmentId: String(appointment.id),
      mobile: appointment.guest_mobile || '',
      name: appointment.guest_name || '',
    });
    window.open(`/patient-registration?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleMapPatient = async match => {
    const confirmResult = await Swal.fire({
      title: 'Link this appointment to the selected patient?',
      html: `<b>${[match.first_name, match.middle_name, match.last_name].filter(Boolean).join(' ')}</b>${match.external_code ? ` (${match.external_code})` : ''}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Map',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;
    setMapping(true);
    try {
      await api.appointment.mapPatient(appointment.id, {
        irm_contacts_id: match.id,
        updated_by: getCurrentUserId(),
      });
      setShowMappingPanel(false);
      refreshAndNotify();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setMapping(false);
    }
  };

  const openPaymentForm = () => {
    setPaymentForm({
      payment_mode: 'CASH',
      amount: opdBill?.balance_amount !== undefined && opdBill?.balance_amount !== null ? String(opdBill.balance_amount) : '',
      reference_no: '',
    });
    setShowPaymentForm(true);
  };

  const handleCollectPayment = async () => {
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      Swal.fire({ icon: 'warning', title: 'Enter a valid amount' });
      return;
    }
    setCollectingPayment(true);
    try {
      await api.opdBilling.collectPayment(opdBill.id, {
        payment_mode: paymentForm.payment_mode,
        amount,
        reference_no: paymentForm.reference_no || undefined,
        created_by: getCurrentUserId(),
      });
      setShowPaymentForm(false);
      Swal.fire({ icon: 'success', title: 'Payment collected', timer: 1500, showConfirmButton: false });
      loadBill();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setCollectingPayment(false);
    }
  };

  const closeSubDialog = () => setActiveDialog(null);
  const onSubDialogDone = () => {
    closeSubDialog();
    refreshAndNotify();
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(appointmentId)}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH, maxWidth: '92vw' } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Appointment Details</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>

          {loading && <Typography variant="body2">Loading…</Typography>}

          {!loading && appointment && (
            <>
              <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL, mb: 0.5 }}>
                {formatAppointmentNo(appointment)}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip
                  label={STATUS_LABELS[appointment.status] || appointment.status}
                  color={STATUS_CHIP_COLOR[appointment.status] || 'default'}
                />
                {appointment.is_system_cancelled === 'Y' && (
                  <Chip label="Auto-cancelled" size="small" variant="outlined" />
                )}
                {appointment.is_guest === 'Y' && (
                  <Chip label={GUEST_CHIP_LABEL} size="small" sx={{ bgcolor: GUEST_CHIP_COLOR, color: '#fff' }} />
                )}
              </Box>

              <Typography variant="subtitle1" fontWeight={700}>
                {resolvePatientName(appointment, appointment.irm_contacts_id)}
              </Typography>
              {resolvePatientMobile(appointment) && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <PhoneIcon sx={{ fontSize: 14, color: '#9aa5b1' }} />
                  <Typography variant="body2" color="textSecondary">
                    {resolvePatientMobile(appointment)}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                {resolveDoctorName(appointment.doctorInfo, appointment.irm_users_id)}
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" columnGap={2} rowGap={0.5} sx={{ mt: 0.5 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CalendarMonthIcon sx={{ fontSize: 15, color: '#9aa5b1' }} />
                  <Typography variant="body2" color="textSecondary">
                    {formatDisplayDate(appointment.appointment_date)}
                  </Typography>
                </Box>
                {appointment.appointment_time && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 15, color: '#9aa5b1' }} />
                    <Typography variant="body2" color="textSecondary">
                      {formatDisplayTime(appointment.appointment_time)}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" alignItems="center" gap={0.5}>
                  <ConfirmationNumberIcon sx={{ fontSize: 15, color: '#9aa5b1' }} />
                  <Typography variant="body2" color="textSecondary">
                    Serial #{appointment.serial_no}
                  </Typography>
                </Box>
              </Box>
              {appointment.reason_for_visit && (
                <Typography variant="body2" sx={{ mt: 1 }}>{appointment.reason_for_visit}</Typography>
              )}
              {appointment.cancellation_reason && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Cancelled: {appointment.cancellation_reason}
                </Typography>
              )}

              {appointment.is_guest === 'Y' && possibleMatches?.length > 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {possibleMatches.length} matching patient{possibleMatches.length > 1 ? 's' : ''} found for this mobile number — use Mapping below to link.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2}>
                {appointment.status === 'SCHEDULED' && appointment.is_guest !== 'Y' && (
                  <ActionIcon title="Check In" icon={<LoginIcon fontSize="inherit" />} color={STATUS_HEX.CHECKED_IN} onClick={handleCheckIn} />
                )}
                {appointment.status === 'SCHEDULED' && appointment.is_guest === 'Y' && (
                  <Tooltip title="Register this guest as a patient (or map them to an existing one) before checking in" arrow>
                    <span>
                      <ActionIcon title="Check In" icon={<LoginIcon fontSize="inherit" />} color="#b0bec5" onClick={() => {}} />
                    </span>
                  </Tooltip>
                )}
                {appointment.is_guest === 'Y' && (
                  <>
                    <ActionIcon title="Register as Patient" icon={<PersonAddIcon fontSize="inherit" />} color="#2e7d32" onClick={handleRegisterAsPatient} />
                    <ActionIcon title="Mapping" icon={<LinkIcon fontSize="inherit" />} color="#00897b" onClick={() => setShowMappingPanel(v => !v)} />
                  </>
                )}
                {appointment.status === 'CHECKED_IN' && (
                  <ActionIcon title="Mark Attended" icon={<MedicalServicesIcon fontSize="inherit" />} color={STATUS_HEX.ATTENDED} onClick={handleAttend} />
                )}
                {appointment.status === 'ATTENDED' && (
                  <ActionIcon title="Complete" icon={<CheckCircleIcon fontSize="inherit" />} color={STATUS_HEX.COMPLETED} onClick={handleComplete} />
                )}
                {appointment.status === 'SCHEDULED' && (
                  <ActionIcon title="Reschedule" icon={<EventNoteIcon fontSize="inherit" />} color={TEAL} onClick={() => setActiveDialog('reschedule')} />
                )}
                {!['COMPLETED', 'CANCELLED'].includes(appointment.status) && (
                  <ActionIcon title="Cancel" icon={<CancelIcon fontSize="inherit" />} color={STATUS_HEX.CANCELLED} onClick={() => setActiveDialog('cancel')} />
                )}
                <ActionIcon title="Re-Appointment" icon={<EventRepeatIcon fontSize="inherit" />} color="#5c6bc0" onClick={() => setActiveDialog('re-appointment')} />
                <ActionIcon title="Follow-Up" icon={<NoteAddIcon fontSize="inherit" />} color="#455a64" onClick={() => setActiveDialog('followup')} />
                {['ATTENDED', 'COMPLETED'].includes(appointment.status) && !appointment.irm_prescriptions_id && (
                  <ActionIcon title="Prescription" icon={<ReceiptLongIcon fontSize="inherit" />} color="#8e24aa" onClick={() => setActiveDialog('prescription')} />
                )}
                {appointment.irm_prescriptions_id && (
                  <ActionIcon
                    title={openingPrescription ? 'Opening…' : 'View & Print'}
                    icon={<PrintIcon fontSize="inherit" />}
                    color="#8e24aa"
                    onClick={handleViewPrescription}
                  />
                )}
              </Box>

              {showMappingPanel && appointment.is_guest === 'Y' && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #eef0f2', bgcolor: '#fafbfc' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 1 }}>
                    Link this guest to an existing patient
                  </Typography>
                  {loadingMatches && <Typography variant="body2" color="textSecondary">Searching…</Typography>}
                  {!loadingMatches && possibleMatches?.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No matching patient found yet for {appointment.guest_mobile}.
                    </Typography>
                  )}
                  {!loadingMatches && possibleMatches?.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {possibleMatches.map(match => (
                        <Box key={match.id} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                          <Typography sx={{ fontSize: '0.78rem' }}>
                            {[match.first_name, match.middle_name, match.last_name].filter(Boolean).join(' ')}
                            {match.external_code ? ` (${match.external_code})` : ''}
                            {match.mobile_number ? ` · ${match.mobile_number}` : ''}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={mapping}
                            onClick={() => handleMapPatient(match)}
                            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL }, textTransform: 'none', flexShrink: 0 }}
                          >
                            {mapping ? 'Mapping…' : 'Mark as Mapped'}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {BILLABLE_STATUSES.includes(appointment.status) && (
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${TEAL} 0%, #005f70 100%)`,
                        boxShadow: `0 2px 8px ${TEAL}55`,
                        flexShrink: 0,
                      }}
                    >
                      <PaymentIcon sx={{ fontSize: 15, color: '#fff' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#2d3748', letterSpacing: 0.2 }}>
                      Payment
                    </Typography>
                  </Box>

                  {loadingBill && <Typography variant="body2" color="textSecondary">Loading bill…</Typography>}

                  {!loadingBill && !opdBill && (
                    <Typography variant="body2" color="textSecondary">Could not load the bill for this appointment.</Typography>
                  )}

                  {!loadingBill && opdBill && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${TEAL}30`,
                        borderLeft: `4px solid ${TEAL}`,
                        bgcolor: '#fbfeff',
                        boxShadow: '0 3px 12px rgba(0,0,0,0.06)',
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography sx={{ fontSize: '0.74rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL }}>
                          {opdBill.bill_no || `#${opdBill.id}`}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.25}>
                          <Chip
                            label={opdBill.status}
                            size="small"
                            color={opdBill.status === 'PAID' ? 'success' : opdBill.status === 'CANCELLED' ? 'error' : 'warning'}
                            sx={{ height: 22, fontSize: '0.64rem', fontWeight: 700, px: 0.5 }}
                          />
                          <BillQuickActionsMenu bill={opdBill} onSuccess={loadBill} />
                        </Box>
                      </Box>

                      {(opdBill.lines || []).map(line => (
                        <Box key={line.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.35 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#546e7a' }}>
                            {line.fee_type === 'REGISTRATION' ? 'Registration Fee' : 'Consultation Fee'}
                            {line.is_waived === 'Y' && (
                              <Typography component="span" sx={{ fontSize: '0.68rem', color: '#8d6e63', ml: 0.5 }}>
                                (waived — {line.waived_reason})
                              </Typography>
                            )}
                          </Typography>
                          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 600 }}>
                            ₹{Number(line.amount).toFixed(2)}
                          </Typography>
                        </Box>
                      ))}

                      <Divider sx={{ my: 1 }} />

                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Total</Typography>
                        <Typography sx={{ fontSize: '0.88rem', fontFamily: 'monospace', fontWeight: 800 }}>₹{Number(opdBill.total_amount).toFixed(2)}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography sx={{ fontSize: '0.76rem', color: '#2e7d32', fontWeight: 600 }}>Paid</Typography>
                        <Typography sx={{ fontSize: '0.76rem', fontFamily: 'monospace', color: '#2e7d32', fontWeight: 600 }}>₹{Number(opdBill.paid_amount).toFixed(2)}</Typography>
                      </Box>
                      {Number(opdBill.balance_amount) > 0 && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mt: 0.75, px: 1, py: 0.5, borderRadius: 1.5, bgcolor: '#fdecea' }}
                        >
                          <Typography sx={{ fontSize: '0.78rem', color: '#c62828', fontWeight: 700 }}>Balance Due</Typography>
                          <Typography sx={{ fontSize: '0.84rem', fontFamily: 'monospace', color: '#c62828', fontWeight: 800 }}>₹{Number(opdBill.balance_amount).toFixed(2)}</Typography>
                        </Box>
                      )}

                      {opdBill.status !== 'PAID' && opdBill.status !== 'CANCELLED' && !showPaymentForm && (
                        <MDButton
                          fullWidth
                          variant="gradient"
                          color="success"
                          startIcon={<CurrencyRupeeIcon />}
                          onClick={openPaymentForm}
                          sx={{ mt: 1.5, mb: 1.25 }}
                        >
                          Collect Payment
                        </MDButton>
                      )}

                      <Box display="flex" gap={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        <ActionIcon
                          title="Print Bill"
                          icon={<DescriptionIcon fontSize="inherit" />}
                          color={TEAL}
                          onClick={() => printOpdBill(opdBill)}
                        />
                        <ActionIcon
                          title="Print Invoice"
                          icon={<PrintIcon fontSize="inherit" />}
                          color="#0288d1"
                          onClick={() => printOpdInvoice(opdBill)}
                        />
                        {(opdBill.payments || []).some(p => p.status === 'PAID') && (
                          <ActionIcon
                            title="Print Receipt"
                            icon={<ReceiptIcon fontSize="inherit" />}
                            color="#7b1fa2"
                            onClick={() => {
                              const paidPayments = (opdBill.payments || []).filter(p => p.status === 'PAID');
                              const latest = paidPayments[paidPayments.length - 1];
                              printOpdReceipt(opdBill, latest);
                            }}
                          />
                        )}
                      </Box>

                      {(opdBill.payments || []).length > 0 && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mt: 1.25,
                            '&:before': { display: 'none' },
                            border: '1px solid #eef0f2',
                            borderRadius: '10px !important',
                            overflow: 'hidden',
                            bgcolor: '#fff',
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                            sx={{ minHeight: 38, '& .MuiAccordionSummary-content': { my: 0.6, alignItems: 'center', gap: 0.75 } }}
                          >
                            <HistoryIcon sx={{ fontSize: 15, color: TEAL }} />
                            <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#2d3748' }}>
                              Payment History
                            </Typography>
                            <Chip
                              label={opdBill.payments.length}
                              size="small"
                              sx={{ height: 16, fontSize: '0.6rem', ml: 0.25, bgcolor: `${TEAL}1a`, color: TEAL, fontWeight: 700 }}
                            />
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                            {opdBill.payments.map(p => (
                              <Box key={p.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.6, borderBottom: '1px solid #f5f5f5' }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip label={p.payment_mode} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                                  <Typography sx={{ fontSize: '0.7rem', color: '#9aa5b1' }}>{formatFullDateTime(p.paid_at)}</Typography>
                                  {p.reference_no && (
                                    <Typography sx={{ fontSize: '0.68rem', color: '#b0bec5' }}>Ref: {p.reference_no}</Typography>
                                  )}
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: '0.76rem',
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    color: p.status === 'CANCELLED' ? '#bdbdbd' : '#2e7d32',
                                    textDecoration: p.status === 'CANCELLED' ? 'line-through' : 'none',
                                  }}
                                >
                                  ₹{Number(p.amount).toFixed(2)}
                                </Typography>
                              </Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {showPaymentForm && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: '#f2f9f3',
                            border: '1px solid #cfe8d2',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          <FormControl size="small" fullWidth>
                            <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                            <Select
                              labelId="payment-mode-label"
                              label="Payment Mode"
                              value={paymentForm.payment_mode}
                              onChange={e => setPaymentForm(f => ({ ...f, payment_mode: e.target.value }))}
                              sx={DROPDOWN_SX}
                            >
                              <MenuItem value="CASH">Cash</MenuItem>
                              <MenuItem value="UPI">UPI</MenuItem>
                              <MenuItem value="CARD">Card</MenuItem>
                              <MenuItem value="ONLINE">Online</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            label="Amount"
                            size="small"
                            type="number"
                            fullWidth
                            value={paymentForm.amount}
                            onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                          />
                          {paymentForm.payment_mode !== 'CASH' && (
                            <TextField
                              label="Reference No (optional)"
                              size="small"
                              fullWidth
                              value={paymentForm.reference_no}
                              onChange={e => setPaymentForm(f => ({ ...f, reference_no: e.target.value }))}
                            />
                          )}
                          <Box display="flex" gap={1}>
                            <Button size="small" onClick={() => setShowPaymentForm(false)} sx={{ textTransform: 'none', color: '#666' }}>
                              Cancel
                            </Button>
                            <MDButton
                              size="small"
                              variant="gradient"
                              color="success"
                              disabled={collectingPayment}
                              onClick={handleCollectPayment}
                            >
                              {collectingPayment ? 'Collecting…' : 'Confirm Payment'}
                            </MDButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                History
              </Typography>
              <StatusTimeline logs={appointment.logs} />
            </>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid #eee', flexShrink: 0 }}>
          <MDButton fullWidth variant="outlined" color="dark" onClick={onClose}>
            Close
          </MDButton>
        </Box>
      </Box>

      {activeDialog === 'reschedule' && (
        <RescheduleDialog appointment={appointment} onClose={closeSubDialog} onDone={onSubDialogDone} />
      )}
      {activeDialog === 'cancel' && (
        <CancelDialog appointment={appointment} onClose={closeSubDialog} onDone={onSubDialogDone} />
      )}
      {activeDialog === 're-appointment' && (
        <ReAppointmentDialog appointment={appointment} onClose={closeSubDialog} onDone={onSubDialogDone} />
      )}
      {activeDialog === 'followup' && (
        <CreateFollowupFromAppointmentDialog appointment={appointment} onClose={closeSubDialog} onDone={onSubDialogDone} />
      )}
      {activeDialog === 'prescription' && (
        <CreatePrescriptionFromAppointmentDialog appointment={appointment} onClose={closeSubDialog} onDone={onSubDialogDone} />
      )}
    </Drawer>
  );
}
